import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { searchItems } from "../search";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
};

interface SelectProps<T extends string = string> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  disabled?: boolean;
  /** Type-to-filter long lists (e.g. categories). Default false. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

type MenuRect = { top: number; left: number; width: number; openUp: boolean };

/**
 * Dropdown custom theo dark theme — thay <select> native (option highlight xanh xấu trên nền tối).
 * Accessible: listbox pattern, điều hướng bàn phím, đóng khi bấm ra ngoài.
 * Scroll ngoài menu → reposition (không close); scroll trong menu → list scroll bình thường.
 */
export function Select<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = "Chọn…",
  ariaLabel,
  id,
  disabled = false,
  searchable = false,
  searchPlaceholder = "Tìm…",
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState<MenuRect | null>(null);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const reactId = useId();
  const listboxId = `${id || reactId}-listbox`;

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    return searchItems(options, query, (option) => ({ name: option.label })).map(({ item }) => item);
  }, [options, query, searchable]);

  const selectedIdx = options.findIndex((o) => o.value === value);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;

  const computeRect = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const searchH = searchable ? 44 : 0;
    const estHeight = Math.min(filtered.length * 40 + 8 + searchH, 300);
    const openUp = spaceBelow < estHeight && r.top > spaceBelow;
    setRect({
      top: openUp ? r.top : r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, searchable ? 200 : r.width),
      openUp,
    });
  };

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    computeRect();
    const hi = filtered.length
      ? Math.max(
          0,
          filtered.findIndex((o) => o.value === value),
        )
      : 0;
    setHighlight(hi < 0 ? 0 : hi);
    setOpen(true);
  };

  const closeMenu = (focusTrigger = true) => {
    setOpen(false);
    setQuery("");
    if (focusTrigger) triggerRef.current?.focus();
  };

  const chooseFiltered = (idx: number) => {
    const opt = filtered[idx];
    if (!opt) return;
    onChange(opt.value);
    closeMenu();
  };

  useLayoutEffect(() => {
    if (open) computeRect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered.length, searchable]);

  // Reset highlight only when filter text changes (not on open — openMenu sets selected idx)
  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Focus search when searchable menu opens
  useEffect(() => {
    if (open && searchable) {
      // rAF so portal mounts first
      const t = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(t);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    const onScroll = (e: Event) => {
      const t = e.target;
      if (t instanceof Node && menuRef.current?.contains(t)) return; // list scroll — keep open
      computeRect();
    };
    const onResize = () => computeRect();
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchable, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const node = menuRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const moveHighlight = (delta: number) => {
    if (!filtered.length) return;
    setHighlight((h) => {
      const next = h + delta;
      if (next < 0) return filtered.length - 1;
      if (next >= filtered.length) return 0;
      return next;
    });
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlight(0);
        break;
      case "End":
        e.preventDefault();
        setHighlight(Math.max(0, filtered.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        chooseFiltered(highlight);
        break;
      case "Escape":
        e.preventDefault();
        closeMenu();
        break;
      case "Tab":
        setOpen(false);
        setQuery("");
        break;
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    onListKeyDown(e);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Space/letters type into input; nav keys go to list
    if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", "Escape"].includes(e.key)) {
      onListKeyDown(e);
    } else if (e.key === "Tab") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="ui-select">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`ui-select__trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-controls={open ? listboxId : undefined}
        disabled={disabled}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`ui-select__value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="material-symbols-outlined ui-select__caret" aria-hidden="true">
          expand_more
        </span>
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            className={`ui-select__menu${searchable ? " has-search" : ""}`}
            style={{
              position: "fixed",
              top: rect.openUp ? undefined : rect.top,
              bottom: rect.openUp ? window.innerHeight - rect.top + 4 : undefined,
              left: rect.left,
              width: rect.width,
            }}
          >
            {searchable && (
              <div className="ui-select__search">
                <span className="material-symbols-outlined ui-select__search-icon" aria-hidden="true">
                  search
                </span>
                <input
                  ref={searchRef}
                  type="search"
                  className="ui-select__search-input"
                  value={query}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                />
              </div>
            )}
            <div
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              className="ui-select__list"
            >
              {filtered.length === 0 ? (
                <div className="ui-select__empty" role="status">
                  Không có kết quả
                </div>
              ) : (
                filtered.map((opt, idx) => {
                  const isSelected = opt.value === value;
                  const isActive = idx === highlight;
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={isSelected}
                      data-idx={idx}
                      className={`ui-select__option${isActive ? " is-active" : ""}${isSelected ? " is-selected" : ""}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        chooseFiltered(idx);
                      }}
                    >
                      <span className="ui-select__option-label">{opt.label}</span>
                      {isSelected && (
                        <span className="material-symbols-outlined ui-select__check" aria-hidden="true">
                          check
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
